import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { BookOpen, ListOrdered, MapPin, Info, Phone, HelpCircle, Shield, FileText, Send } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';
import AdminBrandStoryPage from './brand-story-page/page';
import AdminThreeStepPage from './three-step-page/page';
import AdminWhereToBuyPage from './where-to-buy-page/page';
import AdminAboutPage from './about-page/page';
import AdminContactPage from './contact-page/page';
import AdminFaqPage from './faq-page/page';
import AdminPrivacyPage from './privacy-page/page';
import AdminTermsPage from './terms-page/page';
import AdminPartnerApplyPage from './partner-apply-page/page';

interface WebsiteTab {
  key: string;
  label: string;
  icon: LucideIcon;
  Component: () => React.ReactElement;
}

// Home Page, Products, Header & Footer, Shipping & Returns, and Content
// Hub are edited often enough that they get their own top-level sidebar
// entries instead of living here — see nav.tsx. The shared Settings
// strings (common/cart/stickyBar) also got their own standalone sidebar
// page (settings-page/page.tsx) rather than a tab here, since they
// aren't a "page" the same way these marketing pages are. Only the
// lower-traffic marketing pages remain grouped into this one tabbed
// page, so the sidebar doesn't grow back to 14+ entries.
const TABS: WebsiteTab[] = [
  { key: 'brand-story', label: 'Brand Story', icon: BookOpen, Component: AdminBrandStoryPage },
  { key: 'three-step', label: '3-Step Treatment', icon: ListOrdered, Component: AdminThreeStepPage },
  { key: 'where-to-buy', label: 'Where to Buy', icon: MapPin, Component: AdminWhereToBuyPage },
  { key: 'about', label: 'About', icon: Info, Component: AdminAboutPage },
  { key: 'contact', label: 'Contact', icon: Phone, Component: AdminContactPage },
  { key: 'faq', label: 'FAQ', icon: HelpCircle, Component: AdminFaqPage },
  { key: 'privacy', label: 'Privacy Policy', icon: Shield, Component: AdminPrivacyPage },
  { key: 'terms', label: 'Terms of Service', icon: FileText, Component: AdminTermsPage },
  { key: 'partner-apply', label: 'Partner Apply', icon: Send, Component: AdminPartnerApplyPage },
];

export default function AdminWebsiteHub() {
  const [active, setActive] = useState<string>(TABS[0].key);
  const activeTab = TABS.find((t) => t.key === active) ?? TABS[0];
  const ActiveComponent = activeTab.Component;

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div>
          <h1 className="text-[22px] font-bold text-ink">Pages</h1>
          <p className="text-[12.5px] text-ink-faint mt-0.5">Every other page's text, images, and section visibility — organized by page.</p>
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:thin]">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.key === active;
            return (
              <button
                key={tab.key}
                onClick={() => setActive(tab.key)}
                className={`flex items-center gap-1.5 flex-shrink-0 text-[12.5px] font-semibold px-3.5 py-2 rounded-full cursor-pointer transition-colors whitespace-nowrap ${
                  isActive ? 'bg-primary-800 text-white' : 'bg-surface-muted text-ink-faint hover:text-ink'
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        <div key={activeTab.key}>
          <ActiveComponent />
        </div>
      </div>
    </AppShell>
  );
}
